import { SafePromise } from '@benzinga/safe-await';
import { Subscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import {
  AltOffering,
  AltOfferingCompany,
  AltOfferingCompanySlug,
  AltOfferingSlug,
  OfferingFeedback,
  PaginatedAltOffering,
  OfferingFeedbackResponse,
} from './entities';
import { AltOfferingsRequest, AltOfferingsRestfulEvent } from './request';
import { FilterOptions, AltOfferingsStore } from './store';

export type AltOfferingsManagerEvent = AltOfferingsRestfulEvent;

export class AltOfferingsManager extends Subscribable<AltOfferingsManagerEvent> {
  private request: AltOfferingsRequest;
  private store: AltOfferingsStore;

  constructor(session: Session) {
    super();
    this.request = new AltOfferingsRequest(session);
    this.store = new AltOfferingsStore();
  }

  public static getName = () => 'benzinga-alts';

  public getCompany = async (companySlug: AltOfferingCompanySlug): SafePromise<AltOfferingCompany> => {
    return await this.request.getCompany(companySlug);
  };

  public getOfferings = async (params?: any, force?: boolean): SafePromise<AltOffering[] | undefined> => {
    const stored = this.store.getOfferings();

    if (!stored || force) {
      const offerings = await this.request.getOfferings(params);
      if (offerings.err) {
        return offerings;
      } else {
        if (this.store.updateOfferings(offerings.ok)) {
          this.dispatch({
            offering: offerings.ok,
            type: 'get_offerings',
          });
        }
      }
      return offerings;
    }
    return { ok: stored ?? [] };
  };

  public getReviews = async (params?: FilterOptions): SafePromise<PaginatedAltOffering> => {
    const offerings = await this.request.getReviews(params);
    if (offerings.err) {
      return offerings;
    } else {
      if (this.store.updateOfferings(offerings.ok.offerings)) {
        this.dispatch({
          offering: offerings.ok.offerings,
          type: 'get_offerings',
        });
      }
    }
    return offerings;
  };

  public getListing = async (listingSlug: AltOfferingSlug): SafePromise<AltOffering> => {
    return await this.request.getListing(listingSlug);
  };

  public submitOfferFeedback = async (
    listingSlug: number | string,
    offerFeedback: OfferingFeedback,
  ): SafePromise<OfferingFeedbackResponse> => {
    return await this.request.submitOfferFeedback(listingSlug, offerFeedback);
  };

  public filterOfferings = async (values: FilterOptions): SafePromise<AltOffering[] | undefined> => {
    const { ok: offerings } = await this.getOfferings();

    if (offerings) {
      const filteredOfferings = this.store.filterOfferings(offerings, values);
      return { ok: filteredOfferings };
    }
    return { ok: [] };
  };

  public filterReviewOfferings = async (values: FilterOptions): SafePromise<PaginatedAltOffering> => {
    const res = await this.request.getReviews(values);
    if (res?.ok) {
      return { ok: res.ok };
    }
    return { ok: { offerings: [], total: 0 } };
  };

  public addOfferings = (offerings: AltOffering[]): void => {
    this.store.updateOfferings(offerings);
  };
}
